import { useState, useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { useAtom } from 'jotai'
import {
  Button,
  TextField,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  IconButton,
  CircularProgress,
  Alert,
  Grid,
  Autocomplete,
  Collapse,
  Menu,
} from '@mui/material'
import {
  ArrowDropDown as ArrowDropDownIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PersonAddAlt1 as PersonAddIcon,
  Check as CheckIcon,
} from '@mui/icons-material'
import { JobOpportunity, jobOpportunitiesApi, contactsApi, Contact, ContactInteraction } from '../../services/api'
import { warmContactsAt, contactsForJob, orderWarmContacts, contactDisplayName } from './WarmContactsCell'
import {
  lastInteractionFor,
  sortInteractionsByDate,
  interactionDate,
  interactionTypeLabel,
  formatReachOutAt,
  CONNECTION_TYPE_OPTIONS,
  DEFAULT_CONNECTION_TYPE,
} from '../../constants/networking'
import PanelSection from '../common/PanelSection'
import { Monogram, Avatar } from '../common/Monogram'
import { colors } from '../../theme'
import DialogShell from '../common/DialogShell'
import ConfirmSubmissionDialog from './ConfirmSubmissionDialog'
import { JOB_TABS, jobTabLabel } from './jobTabRouting'
import { buildJobActivity } from './jobActivity'
import { userAtom } from '../../state/auth'
import { useNotification } from '../../hooks/useNotification'
import { formatLocalYMD, todayLocalYMD } from '../../utils/dateFormat'
import { withDecisionDate } from '../../utils/appliedDate'
import { isJobDateInRange, jobDateError } from '../../utils/dateRange'
import { decisionSectionMode } from '../../utils/jobDecisionSection'
import { jobStatusMenu } from '../../utils/jobStatusMenu'
import { isValidJobUrl, ensureHttpProtocol } from '../../utils/validation'
import {
  JOB_STATUS_LABELS,
  CLOSE_REASONS,
  COMPANY_TIER_LABELS,
  COMPANY_TIER_CHIP_COLOR,
  resolveCompanyTier,
  JobOpportunityStatus,
  APPLY_DECISIONS,
  APPLY_DECISION_LABELS,
  resolveJobStatusLabel,
  SIGNAL_TYPE_LABELS,
  SIGNAL_ACTION_LABELS,
  WATCH_TYPE_LABELS,
  effectiveSignalAction,
  resolveWatchType,
  isAwaitingClientDecision,
} from '../../state/jobOpportunities'
import { signalHolderLabel } from '../signals/signalRows'

interface JobDetailDialogProps {
  open: boolean
  job: JobOpportunity | null
  onClose: () => void
  onUpdate: (job: JobOpportunity) => void
  onDelete: (id: string) => void
  // Supplied by a host that can show the interview without navigating: the
  // staff applications page and the client portal's Front Door page both mount
  // the interview dialog alongside this one. A host that cannot omits it, and
  // the button is not drawn.
  onViewInterview?: (interviewId: string) => void
  // MWA-394 — for a host whose onViewInterview takes a moment (the portal reads
  // the interview first). When this is defined the dialog stays open, shows the
  // button as busy while true, and leaves closing to the host once the
  // interview has loaded. Left undefined, the dialog closes first and then
  // calls onViewInterview, as the staff page expects.
  viewInterviewPending?: boolean
  // MWA-297 — the client's contacts, so the dialog can show who can open a
  // door at this company and let staff attach someone the employer-name match
  // will never find. Optional: a host that hasn't loaded contacts simply
  // renders no Contacts section rather than fetching them itself.
  contacts?: Contact[]
  /**
   * MWA-297 — the client's interactions, already loaded by the host in one
   * request. Optional: a host that has not loaded them renders the contact rows
   * without history rather than fetching on their behalf.
   */
  interactions?: ContactInteraction[]
  // MWA-358 — the host's contacts request, so the section can say "loading" or
  // "couldn't load" instead of presenting a failed fetch as an empty network.
  contactsLoading?: boolean
  contactsError?: boolean
  onRetryContacts?: () => void
  // MWA-358 — supplied by a staff host so a contact assigned from here is
  // replaced in the host's list. Without it the dialog offers no assign controls.
  onContactUpdate?: (contact: Contact) => void
}

type StatusChipColor = 'default' | 'primary' | 'success' | 'warning' | 'error'

// Theme colours for the status chip. Statuses not listed (the retired
// 'applying', closed_not_accepting, an unrecognised value) read as neutral.
const JOB_STATUS_CHIP_COLOR: Record<string, StatusChipColor> = {
  applied: 'primary',
  following_up: 'primary',
  interviewing: 'warning',
  got_the_job: 'success',
  rejected: 'error',
}

const PENDING_DECISION_TAB = JOB_TABS.find((t) => t.key === 'pending_decision')!

// How many employer matches show before "Show all". A client can know dozens
// of people at one large employer, and every row is a full contact card.
const MATCH_PREVIEW_COUNT = 5

// Descriptive text under a control or beside a value. The theme's caption is
// weight 600 because it is also the field-label style; copy that explains
// something should read as ordinary text.
const quietSx = { fontWeight: 400 } as const

function FactCell({ label, wide, children }: { label: string; wide?: boolean; children: ReactNode }) {
  return (
    <Box sx={{ minWidth: 0, ...(wide && { gridColumn: '1 / -1' }) }}>
      <Typography variant="caption" color="text.secondary" component="dt" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body1" component="dd" sx={{ m: 0, minWidth: 0, overflowWrap: 'anywhere' }}>
        {children}
      </Typography>
    </Box>
  )
}

export default function JobDetailDialog({
  open,
  job,
  onClose,
  onUpdate,
  onDelete,
  onViewInterview,
  viewInterviewPending,
  contacts,
  interactions,
  contactsLoading,
  contactsError,
  onRetryContacts,
  onContactUpdate,
}: JobDetailDialogProps) {
  const [user] = useAtom(userAtom)
  const { showSuccess } = useNotification()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savingDecision, setSavingDecision] = useState(false)
  // MWA-250 — "Generate with AI" for the job fit score (staff only).
  const [generatingScore, setGeneratingScore] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  // MWA-224 — when a save would advance "Applying" → "Applied", hold the pending
  // updates until staff confirm submission in the dialog.
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false)
  const [pendingUpdates, setPendingUpdates] = useState<{ id: string; [key: string]: unknown } | null>(null)
  // MWA-405 — the header status badge opens this menu.
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(null)
  // MWA-224 — tracks the previously-shown card so a same-card WebSocket refresh
  // doesn't silently cancel an open submission-confirm.
  const prevJobIdRef = useRef<string | null>(null)
  // Mirror `editing` into a ref so the job-change effect can preserve an
  // in-progress edit on a same-card refresh (WS update / AI score generation)
  // without having to re-run whenever the edit toggle flips.
  const editingRef = useRef(false)
  const [formData, setFormData] = useState({
    company: '',
    jobTitle: '',
    applicationDate: '',
    datePosted: '',
    url: '',
    notes: '',
    status: '' as JobOpportunityStatus,
    closeReason: '',
    location: '',
    fitScore: '',
    fitScoreReason: '',
  })
  const isStaff = user?.role !== 'client'
  // MWA-304 — a hidden opportunity is a notice to the client, not a record
  // they work on. update-job-opportunity and delete-job-opportunity already
  // refuse their writes; the controls come off so they never meet a 403 they
  // cannot do anything about.
  const clientReadOnly = !isStaff && job?.hiddenOpportunity === true
  // MWA-304 (F2) — a hidden opportunity has no client decision to record.
  // Staff saw the four decision buttons under "Set or change the client's
  // decision on their behalf", and using them wrote a decision the client
  // was never offered and then saw attributed to themselves.
  const canRecordDecision = isStaff && job?.hiddenOpportunity !== true
  // MWA-297 — saving a contact link. Kept separate from `saving` so the main
  // edit form's buttons don't flicker while a link is being written.
  const [savingLink, setSavingLink] = useState(false)

  // Contacts on this opportunity, and the ones still available to attach.
  // `matchedIds` is what the employer name already found: those rows are
  // derived, not stored, so they carry no remove control — removing one would
  // claim to delete something that isn't there.
  const allContacts = contacts ?? []
  const allInteractions = interactions ?? []
  // One row open at a time: the dialog is already long, and two open histories
  // push the Decision actions off screen at 900px.
  const [expandedContactId, setExpandedContactId] = useState<string | null>(null)
  const jobContacts = job ? contactsForJob(allContacts, job.company, job.linkedContactIds) : []
  // MWA-358 — the employer match, ordered and capped, then the hand-attached
  // referrers, which are always shown: there are only ever a few, and each one
  // is a person staff chose on purpose.
  const matchedContacts = job ? orderWarmContacts(warmContactsAt(allContacts, job.company)) : []
  const matchedIds = new Set(matchedContacts.map((c) => c.id))
  const linkedContacts = jobContacts.filter((c) => !matchedIds.has(c.id))
  const [showAllMatches, setShowAllMatches] = useState(false)
  const visibleContacts = [
    ...(showAllMatches ? matchedContacts : matchedContacts.slice(0, MATCH_PREVIEW_COUNT)),
    ...linkedContacts,
  ]
  const assignedCount = jobContacts.filter((c) => c.reachOutAt).length
  const attachable = allContacts.filter(
    (c) => !c.isDeleted && !jobContacts.some((j) => j.id === c.id)
  )

  // MWA-358 — handing a contact to the client from the opportunity: the same
  // reachOutAt / reachOutReason the contact panel writes, so it shows as
  // "Assigned on" in Networking and counts wherever assignments are counted.
  const canAssign = isStaff && onContactUpdate !== undefined
  const clientFirstName = job?.clientName?.trim().split(/\s+/)[0] || 'the client'
  const [assignFormId, setAssignFormId] = useState<string | null>(null)
  const [assignReason, setAssignReason] = useState('')
  const [assignReasonError, setAssignReasonError] = useState('')
  const [confirmUnassignId, setConfirmUnassignId] = useState<string | null>(null)
  const [assigningId, setAssigningId] = useState<string | null>(null)
  const [assignError, setAssignError] = useState<{ contactId: string; message: string } | null>(null)

  // Written to the client, who reads it in the portal, so it says "you".
  const defaultAssignReason = (c: Contact) => {
    if (!job) return ''
    if (!matchedIds.has(c.id)) {
      return `Knows people at ${job.company} and can introduce you for the ${job.jobTitle} role.`
    }
    return job.hiddenOpportunity
      ? `Works at ${job.company}, where the ${job.jobTitle} role may be opening.`
      : `Works at ${job.company}, where you're pursuing the ${job.jobTitle} role.`
  }

  const openAssignForm = (c: Contact) => {
    setAssignFormId(c.id)
    setAssignReason(defaultAssignReason(c))
    setAssignReasonError('')
    setConfirmUnassignId(null)
    setAssignError(null)
  }

  const writeAssignment = async (
    c: Contact,
    reachOutAt: string | null,
    reachOutReason: string,
    failureMessage: string
  ) => {
    if (!onContactUpdate) return false
    setAssigningId(c.id)
    setAssignError(null)
    try {
      const updated = await contactsApi.update({ id: c.id, reachOutAt, reachOutReason })
      onContactUpdate(updated)
      return true
    } catch (err) {
      console.error(`Failed to update the assignment on contact ${c.id}:`, err)
      setAssignError({ contactId: c.id, message: failureMessage })
      return false
    } finally {
      setAssigningId(null)
    }
  }

  const handleAssign = async (c: Contact) => {
    const reason = assignReason.trim()
    if (!reason) {
      setAssignReasonError('Enter a reason first')
      return
    }
    const saved = await writeAssignment(
      c,
      new Date().toISOString(),
      reason,
      `Couldn't assign ${contactDisplayName(c)}. Your text is still here — try again.`
    )
    if (saved) setAssignFormId(null)
  }

  const handleUnassign = async (c: Contact) => {
    const saved = await writeAssignment(c, null, '', `Couldn't unassign ${contactDisplayName(c)}. Try again.`)
    if (saved) setConfirmUnassignId(null)
  }

  const writeLinks = async (ids: string[]) => {
    if (!job) return
    setSavingLink(true)
    try {
      const updated = await jobOpportunitiesApi.update({ id: job.id, linkedContactIds: ids })
      onUpdate(updated)
    } catch (err) {
      console.error('Failed to update linked contacts:', err)
      setSaveError(err instanceof Error ? err.message : 'Failed to update contacts')
    }
    setSavingLink(false)
  }

  const handleLinkContact = (contact: Contact | null) => {
    if (!contact || !job) return
    writeLinks([...(job.linkedContactIds || []), contact.id])
  }

  const handleUnlinkContact = (contactId: string) => {
    if (!job) return
    writeLinks((job.linkedContactIds || []).filter((id) => id !== contactId))
  }

  useEffect(() => {
    editingRef.current = editing
  }, [editing])

  useEffect(() => {
    if (!job) return
    const isCardSwitch = prevJobIdRef.current !== job.id
    // MWA-297 — the dialog is reused across cards, so an expanded contact would
    // otherwise stay open on the next opportunity, showing one job's history
    // under another's heading.
    if (isCardSwitch) {
      setExpandedContactId(null)
      // MWA-358 — same reason: a half-typed reason must not carry over to
      // another opportunity's contact.
      setShowAllMatches(false)
      setAssignFormId(null)
      setConfirmUnassignId(null)
      setAssignError(null)
    }
    // Reset the form on a real card switch, OR on a same-card refresh only when
    // the user isn't mid-edit. This preserves unsaved edits when an update lands
    // on the open card (e.g. a WebSocket refresh or an AI fit-score generation),
    // instead of silently discarding them and bouncing back to read view.
    if (isCardSwitch || !editingRef.current) {
      setFormData({
        company: job.company,
        jobTitle: job.jobTitle,
        applicationDate: job.applicationDate || '',
        datePosted: job.datePosted || '',
        url: job.url || '',
        notes: job.notes || '',
        status: job.status,
        closeReason: job.closeReason || '',
        location: job.location || '',
        fitScore: job.fitScore != null ? String(job.fitScore) : '',
        fitScoreReason: job.fitScoreReason || '',
      })
    }
    setSaveError(null)
    // MWA-224 — only reset edit state / drop an in-flight submission-confirm when
    // the dialog switches to a different card, not on a same-card refresh.
    if (isCardSwitch) {
      setEditing(false)
      setConfirmDelete(false)
      setConfirmSubmitOpen(false)
      setPendingUpdates(null)
    }
    prevJobIdRef.current = job.id
  }, [job])

  if (!job) return null

  const activity = buildJobActivity(job)

  const applicationDateError = jobDateError(formData.applicationDate)
  const datePostedError = jobDateError(formData.datePosted)

  const persistUpdates = async (updates: { id: string; [key: string]: unknown }) => {
    setSaving(true)
    setSaveError(null)
    try {
      if (Object.keys(updates).length > 1) {
        const updated = await jobOpportunitiesApi.update(updates)
        onUpdate(updated)
        // MWA-281 — the first move into Interviewing makes update-job-opportunity
        // auto-create the Round 1 interview, so confirm both facts in one toast.
        if (updates.status === 'interviewing' && job.status !== 'interviewing') {
          showSuccess('Application moved to Interviewing. Interview prep has started.')
        }
      }
      setEditing(false)
      setConfirmSubmitOpen(false)
      setPendingUpdates(null)
    } catch (error) {
      console.error('Failed to update job application:', error)
      // Surface the reason (e.g. the MWA-224 guardrail 409) instead of failing
      // silently. Close the confirm dialog so the message is visible underneath.
      setSaveError(error instanceof Error ? error.message : 'Failed to update job application')
      setConfirmSubmitOpen(false)
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async () => {
    if (applicationDateError || datePostedError) return
    const updates: { id: string; [key: string]: unknown } = { id: job.id }
    if (formData.company !== job.company) updates.company = formData.company
    if (formData.jobTitle !== job.jobTitle) updates.jobTitle = formData.jobTitle
    if (formData.applicationDate !== (job.applicationDate || '')) updates.applicationDate = formData.applicationDate
    // Cleared back to empty sends null, not '', so the read view's
    // `job.datePosted &&` guard hides the row rather than printing a blank date.
    if (formData.datePosted !== (job.datePosted || '')) {
      updates.datePosted = formData.datePosted || null
    }
    if (formData.url !== (job.url || '')) updates.url = ensureHttpProtocol(formData.url.trim())
    if (formData.notes !== (job.notes || '')) updates.notes = formData.notes
    if (formData.status !== job.status) updates.status = formData.status
    if (formData.closeReason !== (job.closeReason || '')) updates.closeReason = formData.closeReason
    if (formData.location !== (job.location || '')) updates.location = formData.location

    // MWA-250 — fit score (manual entry path). Parse, validate 1-5 integer, allow clearing.
    const trimmedScore = formData.fitScore.trim()
    const parsedScore = trimmedScore === '' ? null : Number(trimmedScore)
    if (parsedScore !== null && (!Number.isInteger(parsedScore) || parsedScore < 1 || parsedScore > 5)) {
      setSaveError('Fit score must be a whole number from 1 to 5.')
      return
    }
    if ((job.fitScore ?? null) !== parsedScore) updates.fitScore = parsedScore
    if (parsedScore === null) {
      // Clearing the score clears its rationale too, so no orphan "why?" lingers.
      if (job.fitScoreReason) updates.fitScoreReason = ''
    } else if (formData.fitScoreReason !== (job.fitScoreReason || '')) {
      updates.fitScoreReason = formData.fitScoreReason
    }

    await submitUpdates(updates)
  }

  // MWA-224 — an application still awaiting the client's decision has no
  // status, and can only advance to "Applied". Block the other status changes
  // with a clear message; advancing to Applied defers to the confirm dialog
  // (which adds submissionConfirmed:true on confirm). Mirrors the same guard
  // in update-job-opportunity, which is the one that actually enforces it.
  //
  // 'applying' is checked alongside the missing status so records that still
  // hold the retired value keep the same gate until the migration runs.
  //
  // Shared by the Edit form's Save and the header status menu (MWA-405), so a
  // status change takes the same guards whichever way it is made.
  const submitUpdates = async (updates: { id: string; [key: string]: unknown }) => {
    const awaitingDecision = isAwaitingClientDecision(job)
    if (awaitingDecision && updates.status && updates.status !== 'applied') {
      setSaveError('An application awaiting the client’s decision can only move to “Applied”.')
      return
    }
    if (updates.status === 'applied' && awaitingDecision) {
      setPendingUpdates(updates)
      setConfirmSubmitOpen(true)
      return
    }

    await persistUpdates(updates)
  }

  const handleStatusPick = async (status: string) => {
    setStatusMenuAnchor(null)
    if (status === job.status) return
    await submitUpdates({ id: job.id, status })
  }

  // MWA-224 — staff confirmed submission; replay the held updates with the flag.
  const confirmSubmitAndSave = async () => {
    if (!pendingUpdates) return
    // MWA-392 — confirming submission is the day the application went in. A date
    // already typed into the same save wins, so it is spread after the default.
    await persistUpdates({ applicationDate: todayLocalYMD(), ...pendingUpdates, submissionConfirmed: true })
  }

  // MWA-250 — generate the fit score with AI. Persists server-side and returns
  // the updated opportunity; the result remains editable afterward.
  // Only the facts this record actually has, built here rather than inline so
  // the read view is a list to render instead of six conditional paragraphs.
  const readFacts = [
    // MWA-397 — a stored date outside the valid range hides its row rather
    // than printing a wrong date.
    job.applicationDate && isJobDateInRange(job.applicationDate) && { label: 'Applied', value: formatLocalYMD(job.applicationDate) },
    job.datePosted && isJobDateInRange(job.datePosted) && { label: 'Posted', value: formatLocalYMD(job.datePosted) },
    job.lastFollowUpDate && isJobDateInRange(job.lastFollowUpDate) && {
      label: 'Last follow-up',
      value: formatLocalYMD(job.lastFollowUpDate),
    },
    job.location && { label: 'Location', value: job.location },
    // MWA-304 — the signal behind a hidden opportunity. Shown to the client
    // as well as staff: their copy is read-only, not thinner.
    job.signalType && {
      label: 'Signal',
      value: SIGNAL_TYPE_LABELS[job.signalType] || job.signalType,
    },
    // MWA-421 — which Signals table it is in, and the two columns the dialog
    // did not have. "None" is shown rather than dropped: it is an answer.
    job.hiddenOpportunity && { label: 'Watch', value: WATCH_TYPE_LABELS[resolveWatchType(job)] },
    job.signalText && { label: 'What happened', value: job.signalText },
    signalHolderLabel(job) && { label: 'Who held the role before', value: signalHolderLabel(job) },
    job.companyLinkedInUrl && { label: 'Company LinkedIn', value: job.companyLinkedInUrl },
    job.signalObservedDate && {
      label: 'Observed',
      value: formatLocalYMD(job.signalObservedDate),
    },
    job.signalSource && { label: 'Source', value: job.signalSource },
    // MWA-355 / MWA-421 — the Action on the signal, shown to both sides. An
    // unset Action reads as Pending rather than dropping the row: a missing
    // line reads as "they said no" to anyone scanning this dialog, which is the
    // one reading MWA-355 called out as wrong. The MWA-355 answer reads
    // through, and keeps its date, until an Action is set over it.
    job.hiddenOpportunity && {
      label: 'Action',
      value: [
        SIGNAL_ACTION_LABELS[effectiveSignalAction(job)],
        !job.signalAction && job.clientPursuit && job.clientPursuitAt
          ? new Date(job.clientPursuitAt).toLocaleDateString()
          : '',
      ]
        .filter(Boolean)
        .join(' · '),
    },
    job.closeReason && {
      label: 'Closed',
      value:
        CLOSE_REASONS.find((r) => r.value === job.closeReason)?.label || job.closeReason,
    },
  ].filter((f): f is { label: string; value: string } => !!f)

  const handleGenerateScore = async () => {
    setGeneratingScore(true)
    setSaveError(null)
    try {
      const updated = await jobOpportunitiesApi.generateFitScore(job.id)
      onUpdate(updated)
      // Reflect the generated values in the edit form too, so a staffer who
      // generated mid-edit sees the new score/rationale in the fields.
      setFormData((p) => ({
        ...p,
        fitScore: updated.fitScore != null ? String(updated.fitScore) : '',
        fitScoreReason: updated.fitScoreReason || '',
      }))
    } catch (error) {
      console.error('Failed to generate job fit score:', error)
      setSaveError(error instanceof Error ? error.message : 'Failed to generate job fit score')
    } finally {
      setGeneratingScore(false)
    }
  }

  // MWA-220 — client records (or changes) their decision on a staff-sourced app.
  const handleDecision = async (decision: string) => {
    setSavingDecision(true)
    setSaveError(null)
    try {
      const updated = await jobOpportunitiesApi.update(withDecisionDate({ id: job.id, applyDecision: decision }))
      onUpdate(updated)
    } catch (error) {
      console.error('Failed to record application decision:', error)
      setSaveError(error instanceof Error ? error.message : 'Failed to record application decision')
    } finally {
      setSavingDecision(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await jobOpportunitiesApi.delete(job.id)
      onDelete(job.id)
      onClose()
    } catch (error) {
      console.error('Failed to delete job application:', error)
    } finally {
      setDeleting(false)
    }
  }

  // The header chip. A job with no status is labelled with the tab it sits in,
  // so a pending or hidden opportunity never renders an empty chip.
  const statusChip: { label: string; color: StatusChipColor } = job.status
    ? { label: resolveJobStatusLabel(job.status), color: JOB_STATUS_CHIP_COLOR[job.status] ?? 'default' }
    : job.hiddenOpportunity
      ? { label: 'Hidden opportunity', color: 'default' }
      : { label: jobTabLabel(PENDING_DECISION_TAB, isStaff ? 'staff' : 'client'), color: 'warning' }

  // MWA-405 — the badge is a one-click status menu for whoever can Edit. Off
  // while editing, because a pick saves straight away and would drop the
  // unsaved form.
  const statusMenu = jobStatusMenu(job, !clientReadOnly)
  const statusBadge = !statusMenu.enabled || editing ? (
    // A legacy status ('offer', 'closed') is labelled by resolveJobStatusLabel
    // rather than shown as the raw token. A job with no status is labelled
    // with the tab it sits in, so the chip is never empty.
    <Chip label={statusChip.label} size="small" variant="outlined" color={statusChip.color} />
  ) : (
    <>
      <Chip
        label={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            {statusChip.label}
            {saving ? (
              <CircularProgress size={12} color="inherit" sx={{ ml: 0.75 }} />
            ) : (
              <ArrowDropDownIcon fontSize="small" sx={{ mr: -0.5 }} />
            )}
          </Box>
        }
        size="small"
        variant="outlined"
        color={statusChip.color}
        onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
        disabled={saving}
        aria-haspopup="menu"
        aria-expanded={Boolean(statusMenuAnchor)}
        aria-label={`Status: ${statusChip.label}. Change status`}
        sx={{ height: { xs: 44, sm: 24 }, borderRadius: { xs: '22px', sm: '12px' } }}
      />
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
        MenuListProps={{ 'aria-label': 'Change status' }}
      >
        {statusMenu.options.map((o) => {
          const current = o.value === job.status
          return (
            <MenuItem
              key={o.value}
              role="menuitemradio"
              aria-checked={current}
              selected={current}
              onClick={() => handleStatusPick(o.value)}
              sx={{ minHeight: 44 }}
            >
              <Box component="span" sx={{ flexGrow: 1 }}>{o.label}</Box>
              {current && <CheckIcon fontSize="small" sx={{ ml: 2 }} />}
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )

  const companyTierChip = (
    <Chip
      label={COMPANY_TIER_LABELS[resolveCompanyTier(job.companyTier)]}
      size="small"
      variant="outlined"
      color={COMPANY_TIER_CHIP_COLOR[resolveCompanyTier(job.companyTier)]}
    />
  )
  const companyTierCaption = (
    <Typography variant="caption" color="text.secondary" sx={quietSx}>
      Set from {isStaff ? "the client's" : 'your'} Dream Job target companies list — edit it there to change.
    </Typography>
  )

  // Interview link. Gated on the link itself, not on the status: bulk
  // import attaches an interview to a job at "Got the Job" as well as
  // at "Interviewing", so a status check hides a real interview.
  // Shown to staff and to clients alike, wherever the host supplies
  // onViewInterview. A draft guide still opens: the interview dialog tells a
  // client it is being reviewed.
  const interviewLink =
    job.linkedInterviewId && onViewInterview ? (
      <Button
        variant="outlined"
        size="small"
        startIcon={viewInterviewPending ? <CircularProgress size={14} color="inherit" /> : <LinkIcon />}
        disabled={viewInterviewPending === true}
        onClick={() => {
          const interviewId = job.linkedInterviewId
          if (!interviewId) return
          if (viewInterviewPending === undefined) onClose()
          onViewInterview(interviewId)
        }}
      >
        {viewInterviewPending ? 'Opening…' : 'View Interview'}
      </Button>
    ) : null

  // The signal behind a hidden opportunity, the client's answer to it, and
  // everything else that describes the record. The signal is the substance of a
  // hidden opportunity, so it reads in the main column; the rest are reference
  // and sit in the rail.
  const signalLabels = ['Signal', 'What happened', 'Seat vacated by', 'Observed', 'Source', 'Client’s answer', 'Your answer']
  const signalFacts = readFacts.filter((f) => signalLabels.includes(f.label))
  const detailFacts = readFacts.filter((f) => !signalLabels.includes(f.label))

  const clientFact = <FactCell label="Client">{job.clientName}</FactCell>
  // Company tier is chosen once, on the company, in the Dream Job
  // target-companies list — never per-application. Always read-only
  // here (no edit control), in both view and edit mode.
  const tierFact = <FactCell label="Company tier">{companyTierChip}</FactCell>

  const jobLinks = (job.url || interviewLink) && (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, mt: 1.75 }}>
      {job.url && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          href={ensureHttpProtocol(job.url)}
          target="_blank"
          rel="noopener noreferrer"
        >
          View Job Posting
        </Button>
      )}
      {interviewLink}
    </Box>
  )

  const rail = (
    <>
      <PanelSection first dense>
        <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', rowGap: 1.75, m: 0 }}>
          {clientFact}
          {tierFact}
          {!editing &&
            detailFacts.map((fact) => (
              <FactCell key={fact.label} label={fact.label}>
                {fact.value}
              </FactCell>
            ))}
        </Box>
        <Box sx={{ mt: 1.5 }}>{companyTierCaption}</Box>
        {editing ? interviewLink && <Box sx={{ mt: 1.75 }}>{interviewLink}</Box> : jobLinks}
      </PanelSection>

      {/* Activity: status changes and client decisions, merged by date, newest first. */}
      {activity.length > 0 && (
        <PanelSection title="Activity" dense>
          <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {activity.map((entry, idx) => {
              const last = idx === activity.length - 1
              return (
                <Box
                  component="li"
                  key={`${entry.kind}-${entry.key}-${entry.at}-${idx}`}
                  sx={{
                    position: 'relative',
                    pl: 2.5,
                    pb: last ? 0 : 2,
                    // The line runs from this entry's dot to the next one.
                    '&::before': last
                      ? undefined
                      : {
                          content: '""',
                          position: 'absolute',
                          left: 4,
                          top: 14,
                          bottom: -2,
                          width: '1px',
                          backgroundColor: 'divider',
                        },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 5,
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      backgroundColor: 'background.paper',
                      border: '2px solid',
                      borderColor: idx === 0 ? 'primary.main' : colors.gray300,
                    },
                  }}
                >
                  <Typography variant="badge" color="text.secondary" component="div">
                    {entry.kind === 'status' ? 'Status' : 'Decision'}
                  </Typography>
                  <Chip
                    label={entry.label}
                    size="small"
                    variant="outlined"
                    color={entry.kind === 'status' ? (JOB_STATUS_CHIP_COLOR[entry.key] ?? 'default') : 'default'}
                    sx={{ fontSize: '0.7rem', height: 20, mt: 0.25 }}
                  />
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ ...quietSx, mt: 0.25 }}>
                    {new Date(entry.at).toLocaleDateString()}
                    {entry.by ? ` · by ${entry.by}` : ''}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </PanelSection>
      )}
    </>
  )

  const decisionMode = decisionSectionMode(job)

  const interactiveDecisionSection = (job.applyDecision ||
    (job.decisionHistory && job.decisionHistory.length > 0) ||
    (user?.role === 'client' && job.decisionRequested) ||
    canRecordDecision) && (
    <PanelSection title="Decision" dense first={!editing && signalFacts.length === 0}>
      <Box>
        {job.applyDecision ? (
          <Typography variant="body1">
            Decision: <strong>{APPLY_DECISION_LABELS[job.applyDecision] || job.applyDecision}</strong>
          </Typography>
        ) : user?.role === 'client' && job.decisionRequested ? (
          <Typography variant="body2" color="text.secondary">
            Please let us know how you'd like to proceed with this application.
          </Typography>
        ) : isStaff ? (
          <Typography variant="body2" color="text.secondary">
            No client decision recorded yet.
          </Typography>
        ) : null}

        {/* MWA-265 — staff hint: this control records the decision for the client. */}
        {canRecordDecision && (
          <Typography variant="caption" color="text.secondary" sx={{ ...quietSx, display: 'block', mt: 0.5 }}>
            Set or change the client's decision on their behalf.
          </Typography>
        )}

        {/* MWA-220 client decision buttons (when a decision is requested);
            MWA-265 staff use the same control to set/change it at any time. */}
        {((user?.role === 'client' && job.decisionRequested) || canRecordDecision) && (
          <Box
            role="group"
            aria-label="Decision"
            sx={{
              mt: 1.5,
              display: { xs: 'grid', sm: 'inline-flex' },
              gridTemplateColumns: '1fr 1fr',
              maxWidth: '100%',
              border: 1,
              borderColor: colors.gray300,
              borderRadius: '8px',
              overflow: 'hidden',
              '& .MuiButton-root': {
                borderRadius: 0,
                px: 1.75,
                minHeight: { xs: 44, sm: 36 },
                color: 'text.secondary',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                borderRight: `1px solid ${colors.gray200}`,
                borderBottom: { xs: `1px solid ${colors.gray200}`, sm: 0 },
                '&:hover': { backgroundColor: 'background.default' },
                '&.Mui-disabled': { color: 'text.disabled' },
              },
              '& .MuiButton-root[aria-pressed="true"]': {
                color: 'primary.contrastText',
                backgroundColor: 'primary.main',
                fontWeight: 600,
                '&:hover': { backgroundColor: 'primary.dark' },
              },
              '& .MuiButton-root:last-of-type': { borderRight: 0 },
              '@media (max-width:599.95px)': {
                '& .MuiButton-root:nth-of-type(2n)': { borderRight: 0 },
                '& .MuiButton-root:nth-last-of-type(-n+2)': { borderBottom: 0 },
              },
            }}
          >
            {APPLY_DECISIONS.map((d) => {
              const selected = job.applyDecision === d.value
              return (
                <Button
                  key={d.value}
                  size="small"
                  aria-pressed={selected}
                  startIcon={selected && !savingDecision ? <CheckIcon fontSize="small" /> : undefined}
                  disabled={savingDecision}
                  onClick={() => handleDecision(d.value)}
                >
                  {savingDecision && selected ? <CircularProgress size={16} color="inherit" /> : d.label}
                </Button>
              )
            })}
          </Box>
        )}
      </Box>
    </PanelSection>
  )

  // MWA-406 — once the job has a status the decision is settled: a plain line,
  // no buttons. The history stays in Activity.
  const decisionSection =
    decisionMode === 'readonly' ? (
      <PanelSection title="Decision" dense first={!editing && signalFacts.length === 0}>
        <Typography variant="body1">
          Client decision:{' '}
          <strong>{job.applyDecision && (APPLY_DECISION_LABELS[job.applyDecision] || job.applyDecision)}</strong>
        </Typography>
      </PanelSection>
    ) : decisionMode === 'interactive' ? (
      interactiveDecisionSection
    ) : null

  const main = (
    <>
      {saveError && (
        <Alert severity="error" onClose={() => setSaveError(null)} sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}

      {editing ? (
        <>
          {/* The modal is far wider than the old 460px drawer, so paired fields
              sit side by side at md+ and stack at xs/sm — same Grid idiom as
              ClientIntakeForm rather than one full-width field per row. */}
          <PanelSection title="The role" first dense>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Company"
                  value={formData.company}
                  onChange={(e) => setFormData((p) => ({ ...p, company: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Job Title"
                  value={formData.jobTitle}
                  onChange={(e) => setFormData((p) => ({ ...p, jobTitle: e.target.value }))}
                  fullWidth
                />
              </Grid>
            </Grid>
          </PanelSection>

          <PanelSection title="Where it stands" dense>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  {/* shrink is the counterpart of displayEmpty below: the Select always renders
                      content, so the label must always be raised or it sits on top of it. */}
                  <InputLabel shrink>Status</InputLabel>
                  <Select
                    value={formData.status ?? ''}
                    label="Status"
                    // An application awaiting the client's decision has no status,
                    // and a MUI Select holding '' renders blank without this — so the
                    // field showed nothing where the board reads "Pending Client
                    // Decision".
                    displayEmpty
                    onChange={(e) => setFormData((p) => ({ ...p, status: e.target.value as JobOpportunityStatus }))}
                  >
                    {/* Names the awaiting-decision state rather than leaving the
                        field blank, and cannot be chosen: the way out of it is the
                        client's decision, which writes the status server-side. */}
                    {!formData.status && (
                      <MenuItem value="" disabled>
                        Awaiting client decision
                      </MenuItem>
                    )}
                    {/* Transitional fallback: a record may still hold a legacy status
                        (e.g. 'offer', 'closed') not present in JOB_STATUS_LABELS until
                        the status migration is applied. Without this, MUI logs an
                        out-of-range value error and the field renders blank. */}
                    {!JOB_STATUS_LABELS[formData.status] && formData.status && (
                      <MenuItem value={formData.status} disabled>{formData.status}</MenuItem>
                    )}
                    {Object.entries(JOB_STATUS_LABELS).map(([value, label]) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* MWA-250 — fit score: manual entry + AI generation (staff only) */}
              {isStaff && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Fit Score (1-5)"
                    type="number"
                    value={formData.fitScore}
                    onChange={(e) => setFormData((p) => ({ ...p, fitScore: e.target.value }))}
                    inputProps={{ min: 1, max: 5, step: 1 }}
                    sx={{ width: 110, mr: 1 }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={generatingScore ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                    onClick={handleGenerateScore}
                    disabled={generatingScore}
                    sx={{ mt: '22px', verticalAlign: 'top' }}
                  >
                    Generate with AI
                  </Button>
                </Grid>
              )}
              {isStaff && (
                <Grid size={12}>
                  <TextField
                    label="Fit Score Rationale (why?)"
                    value={formData.fitScoreReason}
                    onChange={(e) => setFormData((p) => ({ ...p, fitScoreReason: e.target.value }))}
                    multiline
                    // The generator now returns two to three sentences — what is
                    // good about the job and what is questionable — where it used to
                    // return one. Two rows showed a third of it.
                    minRows={3}
                    maxRows={8}
                    fullWidth
                  />
                </Grid>
              )}
              {/* Transitional dual-check: existing records may still hold the
                  legacy 'closed' status until the status migration is applied. */}
              {(formData.status === 'closed' || formData.status === 'closed_not_accepting') && (
                <Grid size={12}>
                  <FormControl fullWidth>
                    <InputLabel>Close Reason (optional)</InputLabel>
                    <Select
                      value={formData.closeReason}
                      label="Close Reason (optional)"
                      onChange={(e) => setFormData((p) => ({ ...p, closeReason: e.target.value }))}
                    >
                      <MenuItem value="">None</MenuItem>
                      {/* Transitional fallback: a legacy record may hold a retired
                          reason (e.g. 'rejected', 'position_filled') not present in
                          CLOSE_REASONS until the status migration is applied. */}
                      {formData.closeReason && !CLOSE_REASONS.some((r) => r.value === formData.closeReason) && (
                        <MenuItem value={formData.closeReason} disabled>{formData.closeReason}</MenuItem>
                      )}
                      {CLOSE_REASONS.map((r) => (
                        <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </PanelSection>

          <PanelSection title="Dates and place" dense>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Date Applied"
                  type="date"
                  value={formData.applicationDate}
                  onChange={(e) => setFormData((p) => ({ ...p, applicationDate: e.target.value }))}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={!!applicationDateError}
                  helperText={applicationDateError}
                />
              </Grid>
              {/* The read view below has always shown "Posted:" when the record
                  carried a datePosted, and update-job-opportunity has always
                  accepted the field — but nothing could set it, on any surface,
                  so it was permanently absent on every job. */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Date Posted"
                  type="date"
                  value={formData.datePosted}
                  onChange={(e) => setFormData((p) => ({ ...p, datePosted: e.target.value }))}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={!!datePostedError}
                  helperText={datePostedError ?? 'When the job was advertised'}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="Location"
                  value={formData.location}
                  onChange={(e) => setFormData((p) => ({ ...p, location: e.target.value }))}
                  fullWidth
                />
              </Grid>
            </Grid>
          </PanelSection>

          <PanelSection title="Reference" dense>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  label="Job Posting URL"
                  value={formData.url}
                  onChange={(e) => setFormData((p) => ({ ...p, url: e.target.value }))}
                  fullWidth
                  error={!isValidJobUrl(formData.url)}
                  helperText={!isValidJobUrl(formData.url) ? 'Please enter a valid URL' : ''}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
            </Grid>
          </PanelSection>
        </>
      ) : (
        signalFacts.length > 0 && (
          <PanelSection title="The signal" first dense>
            <Box
              component="dl"
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                columnGap: 3,
                rowGap: 1.5,
                m: 0,
              }}
            >
              {signalFacts.map((fact) => (
                <FactCell key={fact.label} label={fact.label} wide={fact.label === 'What happened'}>
                  {fact.value}
                </FactCell>
              ))}
            </Box>
          </PanelSection>
        )
      )}

      {/* MWA-220 — Decision (read view for all roles; action row for clients).
          MWA-265 — staff can set/change the client decision on their behalf at any
          time, so the panel is always shown to staff (even with no decision yet). */}
      {decisionSection}

      {/* MWA-250 — Job Fit Score (read view: score + meter + rationale; staff can generate) */}
      {!editing && (job.fitScore != null || isStaff) && (
        <PanelSection
          title="Job Fit Score"
          dense
          first={signalFacts.length === 0 && !decisionSection}
          aside={
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
              {/* MWA-363 — how a score was produced is staff context; the
                  client portal must not present its content as AI-made. */}
              {isStaff && job.fitScore != null && job.fitScoreSource && (
                <Typography variant="caption" color="text.secondary" sx={quietSx}>
                  {job.fitScoreSource === 'ai' ? 'AI-generated' : 'Manual'}
                </Typography>
              )}
              {isStaff && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={generatingScore ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                  onClick={handleGenerateScore}
                  disabled={generatingScore}
                >
                  {job.fitScore != null ? 'Regenerate' : 'Generate with AI'}
                </Button>
              )}
            </Stack>
          }
        >
          {job.fitScore != null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: job.fitScoreReason ? 1.25 : 0 }}>
              <Typography
                component="div"
                sx={{ fontSize: '1.75rem', lineHeight: '2rem', fontWeight: 600, letterSpacing: '-0.02em' }}
              >
                {job.fitScore}
                <Typography
                  component="span"
                  sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.disabled', ml: 0.25, letterSpacing: 0 }}
                >
                  / 5
                </Typography>
              </Typography>
              <Box role="img" aria-label={`Fit score ${job.fitScore} of 5`} sx={{ display: 'flex', gap: '3px' }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <Box
                    key={n}
                    sx={{
                      width: 22,
                      height: 6,
                      borderRadius: 99,
                      backgroundColor:
                        n <= (job.fitScore ?? 0)
                          ? (job.fitScore ?? 0) >= 4
                            ? 'success.main'
                            : (job.fitScore ?? 0) >= 3
                              ? 'warning.main'
                              : 'error.main'
                          : 'divider',
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
          {job.fitScoreReason && (
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '68ch' }}>
              {job.fitScoreReason}
            </Typography>
          )}
          {job.fitScore == null && isStaff && (
            <Typography variant="caption" color="text.secondary" sx={quietSx}>
              No fit score yet — generate one with AI or add it in Edit.
            </Typography>
          )}
        </PanelSection>
      )}

      {/* MWA-297 — who can open a door at this company. Two kinds of row:
          the ones the employer-name match found, which are derived and so
          have no remove control, and the ones staff attached by hand. A
          referrer who works somewhere else only ever appears as the second
          kind. Hidden entirely when the host passed no contacts.
          MWA-358 — a failed or pending contacts request says so; before,
          both read as "nobody in the client's network". */}
      {!editing && contacts !== undefined && (jobContacts.length > 0 || isStaff || contactsError) && (
        <PanelSection
          title="Contacts"
          dense
          aside={
            isStaff && jobContacts.length > 0 ? (
              <Typography variant="caption" color="text.secondary" sx={quietSx}>
                {[
                  matchedContacts.length > 0 &&
                    `${matchedContacts.length} ${matchedContacts.length === 1 ? 'works' : 'work'} here`,
                  linkedContacts.length > 0 && `${linkedContacts.length} attached`,
                  assignedCount > 0 && `${assignedCount} assigned`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            ) : undefined
          }
        >
          {contactsError ? (
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Typography variant="body2" color="error">
                Couldn't load {isStaff ? `${clientFirstName}'s` : 'your'} contacts.
              </Typography>
              {onRetryContacts && (
                <Button size="small" onClick={onRetryContacts} sx={{ minWidth: 0 }}>
                  Retry
                </Button>
              )}
            </Stack>
          ) : contactsLoading && jobContacts.length === 0 ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary" sx={quietSx}>
                Loading contacts…
              </Typography>
            </Stack>
          ) : jobContacts.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={quietSx}>
              Nobody in {isStaff ? "the client's" : 'your'} network is attached to this
              application yet.
            </Typography>
          ) : (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {visibleContacts.map((c) => (
                <Box
                  component="li"
                  key={c.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '32px minmax(0, 1fr)', sm: '32px minmax(0, 1fr) auto' },
                    columnGap: 1.5,
                    py: 1.5,
                    borderTop: 1,
                    borderColor: 'divider',
                    alignItems: 'start',
                    '&:first-of-type': { borderTop: 0, pt: 0.25 },
                  }}
                >
                  <Avatar name={contactDisplayName(c)} />
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
                      <Typography variant="body1" component="span" sx={{ fontWeight: 600 }}>
                        {contactDisplayName(c)}
                      </Typography>
                      {/* The two things the list is ordered by, so the
                          order explains itself. The default level is
                          left unsaid: it is most of every import. */}
                      {isStaff && c.isPriority && (
                        <Chip label="Priority" size="small" color="warning" variant="outlined" />
                      )}
                      {isStaff && (c.connectionType || DEFAULT_CONNECTION_TYPE) !== DEFAULT_CONNECTION_TYPE && (
                        <Chip
                          label={
                            CONNECTION_TYPE_OPTIONS.find((o) => o.value === c.connectionType)?.label ||
                            c.connectionType
                          }
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                    {[c.position, c.company].filter(Boolean).join(' — ') ? (
                      <Typography variant="body2" color="text.secondary" component="div">
                        {[c.position, c.company].filter(Boolean).join(' — ')}
                      </Typography>
                    ) : null}
                    {/* MWA-297 — knowing WHO the route in is, without
                        knowing whether it has been walked, is the "list
                        of names" problem this ticket exists to fix. The
                        last conversation stays on screen; the rest is
                        one click, so the Decision actions below stay
                        reachable at 900px. */}
                    {(() => {
                      const mine = allInteractions.filter((i) => i.contactId === c.id)
                      const last = lastInteractionFor(allInteractions, c.id)
                      if (!last) {
                        return (
                          <Typography variant="caption" color="text.secondary" display="block" sx={quietSx}>
                            No conversations logged yet
                          </Typography>
                        )
                      }
                      const isOpen = expandedContactId === c.id
                      return (
                        <>
                          <Button
                            size="small"
                            variant="text"
                            aria-expanded={isOpen}
                            aria-label={`${isOpen ? 'Hide' : 'Show'} conversations with ${contactDisplayName(c)}`}
                            endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            onClick={() => setExpandedContactId(isOpen ? null : c.id)}
                            sx={{ px: 0, minWidth: 0, textTransform: 'none', fontWeight: 400, display: 'flex' }}
                          >
                            Last spoke {new Date(interactionDate(last)).toLocaleDateString()} ·{' '}
                            {interactionTypeLabel(last.type)}
                            {mine.length > 1 ? ` · ${mine.length} in total` : ''}
                          </Button>
                          <Collapse in={isOpen} unmountOnExit>
                            <Stack
                              spacing={1}
                              sx={{ pl: 1.5, ml: 0.25, mt: 0.5, pb: 1, borderLeft: 1, borderColor: 'divider' }}
                            >
                              {sortInteractionsByDate(mine).map((i) => (
                                <Box key={i.id}>
                                  <Typography variant="caption" color="text.secondary" sx={quietSx}>
                                    {new Date(interactionDate(i)).toLocaleDateString()} ·{' '}
                                    {interactionTypeLabel(i.type)}
                                  </Typography>
                                  {i.notes && (
                                    <Typography variant="body2">{i.notes}</Typography>
                                  )}
                                </Box>
                              ))}
                            </Stack>
                          </Collapse>
                        </>
                      )
                    })()}
                    {canAssign && formatReachOutAt(c.reachOutAt) && (
                      <>
                        <Typography variant="caption" color="success.dark" display="block" sx={{ mt: 0.5 }}>
                          Assigned on {formatReachOutAt(c.reachOutAt)}
                        </Typography>
                        {c.reachOutReason && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ ...quietSx, fontStyle: 'italic' }}
                          >
                            “{c.reachOutReason}”
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ flexShrink: 0, mt: { xs: 0.75, sm: 0 }, gridColumn: { xs: '2 / -1', sm: 'auto' } }}
                  >
                    {canAssign && !c.reachOutAt && assignFormId !== c.id && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PersonAddIcon fontSize="small" />}
                        onClick={() => openAssignForm(c)}
                        aria-label={`Assign ${contactDisplayName(c)} to ${clientFirstName}`}
                        sx={{ minHeight: { xs: 44, sm: 30 } }}
                      >
                        Assign
                      </Button>
                    )}
                    {canAssign && c.reachOutAt && confirmUnassignId !== c.id && (
                      <Button
                        size="small"
                        onClick={() => {
                          setConfirmUnassignId(c.id)
                          setAssignFormId(null)
                          setAssignError(null)
                        }}
                        aria-label={`Unassign ${contactDisplayName(c)}`}
                        sx={{ minHeight: { xs: 44, sm: 30 } }}
                      >
                        Unassign
                      </Button>
                    )}
                    {matchedIds.has(c.id) ? (
                      <Chip label="Works here" size="small" variant="outlined" />
                    ) : isStaff ? (
                      <IconButton
                        size="small"
                        aria-label={`Remove ${contactDisplayName(c)} from this application`}
                        onClick={() => handleUnlinkContact(c.id)}
                        disabled={savingLink}
                        sx={{ width: { xs: 44, sm: 30 }, height: { xs: 44, sm: 30 } }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </Stack>
                  {canAssign && assignFormId === c.id && (
                    <Box
                      sx={{
                        gridColumn: '2 / -1',
                        mt: 1,
                        p: 1.5,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: '8px',
                        backgroundColor: 'background.default',
                      }}
                    >
                      <TextField
                        label={`Reason ${clientFirstName} will see`}
                        value={assignReason}
                        onChange={(e) => {
                          setAssignReason(e.target.value)
                          if (assignReasonError) setAssignReasonError('')
                        }}
                        error={Boolean(assignReasonError)}
                        helperText={assignReasonError || undefined}
                        size="small"
                        multiline
                        minRows={2}
                        fullWidth
                        autoFocus
                      />
                      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.25 }}>
                        <Button size="small" onClick={() => setAssignFormId(null)} disabled={assigningId === c.id}>
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAssign(c)}
                          disabled={assigningId === c.id}
                        >
                          {assigningId === c.id ? 'Assigning…' : 'Assign'}
                        </Button>
                      </Stack>
                    </Box>
                  )}
                  {canAssign && confirmUnassignId === c.id && (
                    <Alert
                      severity="warning"
                      variant="outlined"
                      sx={{ gridColumn: '2 / -1', mt: 1 }}
                      action={
                        <Stack direction="row" spacing={0.5}>
                          <Button
                            size="small"
                            color="inherit"
                            onClick={() => setConfirmUnassignId(null)}
                            disabled={assigningId === c.id}
                          >
                            Keep
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleUnassign(c)}
                            disabled={assigningId === c.id}
                          >
                            {assigningId === c.id ? 'Unassigning…' : 'Unassign'}
                          </Button>
                        </Stack>
                      }
                    >
                      Unassign {contactDisplayName(c)}? This clears the date and the reason{' '}
                      {clientFirstName} sees.
                    </Alert>
                  )}
                  {assignError?.contactId === c.id && (
                    <Typography variant="caption" color="error" role="alert" sx={{ gridColumn: '2 / -1', pt: 1 }}>
                      {assignError.message}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
          {!contactsError && matchedContacts.length > MATCH_PREVIEW_COUNT && (
            <Button
              size="small"
              onClick={() => setShowAllMatches((v) => !v)}
              aria-expanded={showAllMatches}
              sx={{ px: 0, minWidth: 0, textTransform: 'none' }}
            >
              {showAllMatches ? 'Show fewer' : `Show all ${matchedContacts.length} at ${job.company}`}
            </Button>
          )}
          {isStaff && !contactsError && !(contactsLoading && jobContacts.length === 0) && (
            <Autocomplete
              options={attachable}
              getOptionLabel={contactDisplayName}
              value={null}
              disabled={savingLink}
              onChange={(_, value) => handleLinkContact(value)}
              sx={{ mt: 1.5 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Attach a contact"
                  helperText="Someone who can make an introduction here, even if they work elsewhere."
                />
              )}
            />
          )}
        </PanelSection>
      )}

      {!editing && job.notes && (
        <PanelSection title="Notes" dense>
          <Typography variant="body2" color="text.secondary">
            {job.notes}
          </Typography>
        </PanelSection>
      )}
    </>
  )

  return (
    <>
    <DialogShell
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullScreenBelow={768}
      title={job.company}
      subtitle={job.jobTitle}
      mono={<Monogram name={job.company} />}
      closeLabel="Close job details"
      extra={statusBadge}
      footerStart={
        confirmDelete ? (
          <Typography variant="body2" role="alert" sx={{ color: 'error.dark', fontWeight: 500 }}>
            This will permanently delete this job application. It cannot be undone.
          </Typography>
        ) : undefined
      }
      footer={
        confirmDelete ? (
          <>
            <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
              {deleting ? <CircularProgress size={16} /> : 'Delete'}
            </Button>
          </>
        ) : (
          <>
            {!clientReadOnly && (
              <IconButton
                color="error"
                onClick={() => setConfirmDelete(true)}
                aria-label="Delete job application"
                sx={{ mr: 'auto', width: { xs: 44, sm: 36 }, height: { xs: 44, sm: 36 } }}
              >
                <DeleteIcon />
              </IconButton>
            )}
            {editing ? (
              <>
                <Button onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
                <Button
                  onClick={handleSave}
                  variant="contained"
                  disabled={saving || !isValidJobUrl(formData.url) || !!applicationDateError || !!datePostedError}
                >
                  {saving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={onClose}>Close</Button>
                {!clientReadOnly && (
                  <Button onClick={() => setEditing(true)} variant="outlined">Edit</Button>
                )}
              </>
            )}
          </>
        )
      }
      split={{ main, rail }}
    >
      {null}
    </DialogShell>

    <ConfirmSubmissionDialog
      open={confirmSubmitOpen}
      job={job}
      submitting={saving}
      onConfirm={confirmSubmitAndSave}
      onCancel={() => {
        setConfirmSubmitOpen(false)
        setPendingUpdates(null)
      }}
    />
    </>
  )
}
